import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useGetAgents } from '@/api/control/control-plane/control-plane'
import type { AgentDto, AgentDtoStatus } from '@/api/control/missionImpactAPI.schemas'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { QueryErrorState } from '@/components/common/QueryErrorState'
import { PageHeader } from '@/components/common/PageHeader'
import { CreateAgentDialog } from '@/components/agents/CreateAgentDialog'
import { EditAgentDialog } from '@/components/agents/EditAgentDialog'
import { DeleteAgentDialog } from '@/components/agents/DeleteAgentDialog'
import { RegenerateTokenDialog } from '@/components/agents/RegenerateTokenDialog'
import { ToggleAgentStatusButton } from '@/components/agents/ToggleAgentStatusButton'
import { AgentHealthStatus } from '@/components/agents/AgentHealthStatus'
import { ConfirmAgentHealthButton } from '@/components/agents/ConfirmAgentHealthButton'
import { getHealthCheckRefreshInterval } from '@/lib/agentHealth'
import { Plus, Download, AlertTriangle } from 'lucide-react'
import { useRoles } from '@/contexts/RolesContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { useAgentManifest } from '@/hooks/useAgentManifest'
import { isVersionOutdated } from '@/services/versionUtils'

/**
 * Returns a relative time string for a given UTC date string.
 */
function getRelativeTime(dateStr: string | null | undefined, t: ReturnType<typeof useLanguage>['t']): string {
  if (!dateStr) return t.agents.relativeTime.never

  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then

  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return t.agents.relativeTime.justNow

  const hours = Math.floor(diffMs / 3_600_000)
  if (hours < 1) return t.agents.relativeTime.minutesAgo.replace('{count}', String(minutes))

  const days = Math.floor(diffMs / 86_400_000)
  if (days < 1) return t.agents.relativeTime.hoursAgo.replace('{count}', String(hours))

  return t.agents.relativeTime.daysAgo.replace('{count}', String(days))
}

/**
 * Returns the badge variant for an agent status string.
 */
function getStatusBadgeVariant(status: AgentDtoStatus): 'default' | 'secondary' | 'destructive' {
  switch (status) {
    case 'Active':
      return 'default'
    case 'Unregistered':
      return 'secondary'
    case 'Inactive':
      return 'destructive'
    default:
      return 'secondary'
  }
}

/**
 * Page for viewing and managing self-hosted agents.
 * All roles can view agents. Admin and Operator roles can create, update, and delete agents.
 */
export default function AgentsPage() {
  const { orgId } = useParams<{ orgId: string }>()
  const navigate = useNavigate()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const { isAdminOrOperator } = useRoles()
  const { t, language } = useLanguage()

  const { data: agentsResponse, isLoading, error, refetch } = useGetAgents(orgId ?? '', {
    query: {
      refetchInterval: (query) => getHealthCheckRefreshInterval(query.state.data?.data, Date.now()),
    },
  })
  const agents = useMemo(() => agentsResponse?.data ?? [], [agentsResponse])
  const { latestVersion } = useAgentManifest()

  const locale = useMemo(() => {
    switch (language) {
      case 'fr':
        return 'fr-FR'
      case 'de':
        return 'de-DE'
      case 'es':
        return 'es-ES'
      default:
        return 'en-US'
    }
  }, [language])

  if (!orgId) {
    return null
  }

  return (
    <div className="space-y-6">
      <PageHeader title={t.agents.title} description={t.agents.description}>
        {isAdminOrOperator && (
          <>
            <Button variant="outline" asChild>
              <a href={latestVersion ? `/agent/DataMissionAgent-${latestVersion}.msi` : '/agent/DataMissionAgent-latest.msi'}>
                <Download className="mr-2 h-4 w-4" />
                {t.agents.downloadLatest}
              </a>
            </Button>
            <Button onClick={() => { setCreateDialogOpen(true); }}>
              <Plus className="mr-2 h-4 w-4" />
              {t.agents.addAgent}
            </Button>
          </>
        )}
      </PageHeader>

      <div className="rounded-lg border bg-card text-card-foreground shadow-sm overflow-hidden">
        {error ? (
          <div className="p-8">
            <QueryErrorState
              error={error}
              message={t.agents.errors.failedToLoadAgents}
              onRetry={() => { void refetch(); }}
            />
          </div>
        ) : isLoading ? (
          <div className="p-8 text-center text-muted-foreground">{t.agents.loadingAgents}</div>
        ) : agents.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            {t.agents.noAgentsFound}
          </div>
        ) : (
          <Table className="[&_tr>*:first-child]:pl-8 [&_tr>*:last-child]:pr-8">
            <TableHeader>
              <TableRow>
                <TableHead>{t.agents.table.name}</TableHead>
                <TableHead>{t.agents.table.status}</TableHead>
                <TableHead>{t.agents.health.label}</TableHead>
                <TableHead>{t.agents.table.version}</TableHead>
                <TableHead>{t.agents.table.lastHeartbeat}</TableHead>
                <TableHead>{t.agents.table.registeredAt}</TableHead>
                <TableHead>{t.agents.table.created}</TableHead>
                <TableHead>{t.agents.table.lastError}</TableHead>
                {isAdminOrOperator && (
                  <TableHead className="w-20 text-right">
                    {t.agents.table.actions}
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent: AgentDto) => (
                <TableRow
                  key={agent.id}
                  className="cursor-pointer"
                  onClick={() => { void navigate(`/dashboard/orgs/${orgId}/agents/${agent.id}`); }}
                >
                  <TableCell className="font-medium">{agent.name}</TableCell>
                  <TableCell>
                    <Badge variant={getStatusBadgeVariant(agent.status)}>
                      {t.agents.status[agent.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <AgentHealthStatus agent={agent} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      {agent.version ?? '-'}
                      {isVersionOutdated(agent.version, latestVersion) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <AlertTriangle className="h-4 w-4 text-warning" />
                          </TooltipTrigger>
                          <TooltipContent>
                            {t.agents.versionOutdated} (v{latestVersion})
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {getRelativeTime(agent.lastHeartbeatUtc, t)}
                  </TableCell>
                  <TableCell>
                    {agent.registeredAtUtc
                      ? new Date(agent.registeredAtUtc).toLocaleDateString(locale, {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {new Date(agent.createdUtc).toLocaleDateString(locale, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs">
                    {agent.lastErrorUtc ? (
                      <span className="text-destructive">{new Date(agent.lastErrorUtc).toLocaleString()}</span>
                    ) : null}
                  </TableCell>
                  {isAdminOrOperator && (
                    <TableCell onClick={(e) => { e.stopPropagation(); }}>
                      <div className="flex justify-end gap-1">
                        <ConfirmAgentHealthButton orgId={orgId} agent={agent} />
                        <EditAgentDialog orgId={orgId} agent={agent} />
                        <ToggleAgentStatusButton orgId={orgId} agent={agent} />
                        {agent.status === 'Unregistered' && (
                          <RegenerateTokenDialog orgId={orgId} agent={agent} />
                        )}
                        <DeleteAgentDialog orgId={orgId} agent={agent} />
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {isAdminOrOperator && (
        <CreateAgentDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          orgId={orgId}
        />
      )}
    </div>
  )
}
